import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

/*
 * 고객사 관리 모달
 * props:
 * - open: boolean
 * - onClose: () => void
 * - onChange: (companies: { id: number; name: string }[]) => void
 */
const AddClientModal = ({
  open,
  onClose,
  onChange,
  initialName
}) => {
  // 고객사 목록
  const [companies, setCompanies] = useState([]);
  // 편집 인덱스
  const [editIndex, setEditIndex] = useState(-1);
  // 편집 값
  const [editValue, setEditValue] = useState('');
  // 새 고객사 이름
  const [newValue, setNewValue] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // 모달 열릴 때 DB에서 로드
  useEffect(() => {
    if (open) {
      fetch('/api/clients')
        .then(res => res.json())
        .then(data => setCompanies(data))
        .catch(err => console.error('고객사 로드 실패', err));
      setEditIndex(-1);
      setEditValue('');
      setNewValue('');
      setNewValue(initialName);
    }
  }, [open]);

  // 추가
  const handleAddNew = async () => {
    const name = newValue.trim();
    if (!name) return;
    try {
      const res = await fetch('/api/clients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientName: name })
      });
      if (!res.ok) throw new Error();
      const created = await res.json();
      const updated = [created, ...companies];
      setCompanies(updated);
      setNewValue('');
      onChange(updated);
      setSnackbarMessage('고객사 추가 성공');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('고객사 추가 실패', err);
      setSnackbarMessage('고객사 추가 실패');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // 편집 시작
  const handleEditClick = idx => {
    setEditIndex(idx);
    setEditValue(companies[idx].clientName);
  };

  // 편집 저장
  const handleEditSave = async idx => {
    const { id } = companies[idx];
    const name = editValue.trim();
    if (!name) return;
    try {
      const res = await fetch(`/api/clients/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, clientName: name })
      });
      if (!res.ok) throw new Error();
      const updatedDto = await res.json();
      const updated = [...companies];
      updated[idx] = updatedDto;
      setCompanies(updated);
      setEditIndex(-1);
      setEditValue('');
      onChange(updated);
      setSnackbarMessage('고객사 수정 성공');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('고객사 수정 실패', err);
      setSnackbarMessage('고객사 수정 실패');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // 편집 취소
  const handleEditCancel = () => {
    setEditIndex(-1);
    setEditValue('');
  };

  // 삭제
  const handleDelete = async idx => {
    const { id } = companies[idx];
    try {
      const res = await fetch(`/api/clients/${id}`, { method: 'DELETE' });
      if (!window.confirm(`정말 "${companies[idx].clientName}" 고객사를 삭제하시겠습니까?`)) return;
      if (!res.ok) throw new Error(); 
      const updated = companies.filter((_, i) => i !== idx);
      setCompanies(updated);
      onChange(updated);
      setSnackbarMessage('고객사 삭제 성공');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('고객사 삭제 실패', err);
      setSnackbarMessage('고객사 삭제 실패');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" sx={{zIndex: 10000}}>
      <DialogTitle>고객사 관리</DialogTitle>
      <DialogContent>
        <Box display="flex" alignItems="center" mb={2} mt={2}>
          <TextField
            label="새 고객사 추가"
            value={newValue}
            onChange={e => setNewValue(e.target.value)}
            fullWidth
          />
          <IconButton color="primary" onClick={handleAddNew} sx={{ ml: 1 }}>
            <AddIcon />
          </IconButton>
        </Box>
        <List>
          {companies.map((c, idx) => (
            <ListItem key={c.id} disableGutters secondaryAction={
              editIndex === idx ? (
                <>
                  <IconButton edge="end" onClick={() => handleEditSave(idx)}>
                    <SaveIcon />
                  </IconButton>
                  <IconButton edge="end" onClick={handleEditCancel}>
                    <CancelIcon />
                  </IconButton>
                </>
              ) : (
                <>
                  <IconButton edge="end" onClick={() => handleEditClick(idx)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton edge="end" onClick={() => handleDelete(idx)}>
                    <DeleteIcon />
                  </IconButton>
                </>
              )
            }>
              {editIndex === idx ? (
                <TextField
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  fullWidth
                />
              ) : (
                <ListItemText primary={c.clientName} />
              )}
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
    {/* 스낵바 알림 */}
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={3000}
      onClose={handleSnackbarClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ zIndex: 10001 }}
    >
      <Alert
        onClose={handleSnackbarClose}
        severity={snackbarSeverity}
        sx={{ width: '100%' }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export default AddClientModal;